/**
 * KALENDERREMSAN OCH MÅNADSRUTNÄTEN.
 *
 * <Kalenderremsa> ÄR DEN SOM BÄR ETT TILLSTÅND. Beslut 04: veckan är enda
 * periodbegreppet i tidrapporteringen — attest per vecka, lås per vecka, ISO
 * 'YYYY-Www'. Remsan är sju dagar, den vet vilken ISO-vecka den visar, och den
 * är det som står bredvid "Lämna in", "Godkänn" och "Lås".
 *
 * <Manadsrutnat> ÄR NAVIGERING. Prototypen har både en stor och en liten
 * månadskalender, och de är ett sätt att bläddra sig till en dag. De bär inget
 * tillstånd och får ingen låsning per månad: den operationen finns inte i
 * kontraktet, där det heter 'week:lock', en vecka i taget. Se LUCKOR i
 * överlämningen — prototypen ritar en sådan knapp ändå.
 *
 * TONEN PÅ EN DAG är densamma i båda:
 *   'ok'      full dag rapporterad        --good
 *   'kort'    något rapporterat, men lite --warn   (prototypens `low`)
 *   'saknas'  arbetsdag utan rapport      --accent, streckad
 *   'tom'     inget förväntat (helg)      dämpad
 */

import { dagarIVecka, veckodagKort, visaVecka } from '../app/datum';
import type { IsoWeek, WorkDate } from '@delat/types';
import { Ikon } from './Ikon';

export type Dagston = 'ok' | 'kort' | 'saknas' | 'tom';

const TONKLASS: Record<Dagston, string> = {
  ok: ' ok',
  kort: ' low',
  saknas: ' miss',
  tom: ' tom',
};

export interface Dagsuppgift {
  readonly ton: Dagston;
  /** Talet i rutan — "8,5" eller "" . Färdigformaterat. */
  readonly text?: string;
}

export interface Kalenderremseprops {
  readonly vecka: IsoWeek;
  /** Uppslag per arbetsdag. Dagar som saknas ritas som 'tom'. */
  readonly dagar: Readonly<Partial<Record<string, Dagsuppgift>>>;
  readonly vald?: WorkDate;
  readonly onValj?: (dag: WorkDate) => void;
}

/** `.week` / `.day` — veckans sju dagar. */
export const Kalenderremsa = ({ vecka, dagar, vald, onValj }: Kalenderremseprops) => (
  <div className="week" role="group" aria-label={visaVecka(vecka)}>
    {dagarIVecka(vecka).map((dag) => {
      const uppgift = dagar[dag] ?? { ton: 'tom' as const };
      return (
        <button
          key={dag}
          type="button"
          className={`day${TONKLASS[uppgift.ton]}`}
          aria-pressed={dag === vald}
          aria-label={dag}
          disabled={onValj === undefined}
          onClick={onValj === undefined ? undefined : () => onValj(dag)}
        >
          <small>{veckodagKort(dag)}</small>
          {uppgift.text ?? dag.slice(8)}
        </button>
      );
    })}
  </div>
);

/**
 * VECKONAVIGERINGEN — pil, veckonummer, summa, pil. Prototypens `.mnav`, men
 * med VECKA i stället för månad, eftersom det är veckan som är periodenheten.
 */
export const Veckonavigering = ({
  vecka,
  summa,
  onForegaende,
  onNasta,
}: {
  readonly vecka: IsoWeek;
  /** Färdigformaterad summa: "38,5 h". */
  readonly summa?: string;
  readonly onForegaende: () => void;
  readonly onNasta: () => void;
}) => (
  <div className="mnav">
    <button type="button" className="iconbtn" aria-label="Föregående vecka" onClick={onForegaende}>
      <Ikon namn="tillbaka" storlek={18} tjocklek={2} />
    </button>
    <b>{visaVecka(vecka)}</b>
    <button type="button" className="iconbtn" aria-label="Nästa vecka" onClick={onNasta}>
      <Ikon namn="fram" storlek={18} tjocklek={2} />
    </button>
    {summa !== undefined && <span className="tot">{summa}</span>}
  </div>
);

/* == Månadsrutnäten ====================================================== */

export interface Manadsdag {
  readonly dag: WorkDate;
  readonly ton: Dagston;
  /** Bara i det STORA rutnätet: brickan under datumet ("8,5 h", "saknas"). */
  readonly etikett?: string;
  readonly idag?: boolean;
  readonly helg?: boolean;
}

const VECKODAGSRAD = ['M', 'T', 'O', 'T', 'F', 'L', 'S'] as const;

/**
 * `.storkal` — det stora månadsrutnätet. NAVIGERING, inte tillstånd.
 * `tomma` är antalet blanka rutor före den första dagen (månadens första
 * måndagsavstånd), så att den 1:a hamnar under rätt veckodag.
 */
export const Manadsrutnat = ({
  dagar,
  tomma,
  vald,
  onValj,
}: {
  readonly dagar: readonly Manadsdag[];
  readonly tomma: number;
  readonly vald?: WorkDate;
  readonly onValj?: (dag: WorkDate) => void;
}) => (
  <div className="storkal">
    {VECKODAGSRAD.map((bokstav, i) => (
      <div className="skhead" key={`${bokstav}${i}`}>
        {bokstav}
      </div>
    ))}
    {Array.from({ length: tomma }, (_, i) => (
      <div className="skday utanfor" key={`tom${i}`} />
    ))}
    {dagar.map((d) => (
      <button
        key={d.dag}
        type="button"
        className={
          `skday${TONKLASS[d.ton]}` +
          (d.helg === true ? ' helg' : '') +
          (d.idag === true ? ' idag' : '')
        }
        aria-pressed={d.dag === vald}
        aria-label={d.dag}
        disabled={onValj === undefined}
        onClick={onValj === undefined ? undefined : () => onValj(d.dag)}
      >
        <span className="d">{Number(d.dag.slice(8))}</span>
        {d.etikett !== undefined && <span className="tag">{d.etikett}</span>}
      </button>
    ))}
  </div>
);

/** `.kgrid` — det lilla månadsrutnätet på startsidan. Bara prickar. */
export const Litetmanadsrutnat = ({
  dagar,
  tomma,
  onValj,
}: {
  readonly dagar: readonly Manadsdag[];
  readonly tomma: number;
  readonly onValj?: (dag: WorkDate) => void;
}) => (
  <>
    <div className="kgrid" aria-hidden="true">
      {VECKODAGSRAD.map((bokstav, i) => (
        <div className="khead" key={`${bokstav}${i}`}>
          {bokstav}
        </div>
      ))}
    </div>
    <div className="kgrid">
      {Array.from({ length: tomma }, (_, i) => (
        <button className="kday utanfor" type="button" tabIndex={-1} key={`tom${i}`} />
      ))}
      {dagar.map((d) => (
        <button
          key={d.dag}
          type="button"
          className={
            `kday${TONKLASS[d.ton]}` +
            (d.helg === true ? ' helg' : '') +
            (d.idag === true ? ' idag' : '')
          }
          aria-label={d.dag}
          disabled={onValj === undefined}
          onClick={onValj === undefined ? undefined : () => onValj(d.dag)}
        >
          {Number(d.dag.slice(8))}
          <span className="pt" />
        </button>
      ))}
    </div>
  </>
);
